import {
  Grid,
  Heading,
  Button,
  Marker,
  Caption,
  Card,
  Table,
  Skeleton,
  DateFormatter,
} from '@design-systems/canvas-ui'
import Icon, {
  ChevronLeftIcon,
  PencilIcon,
  PersonGroupIcon,
} from '@design-systems/react-icons'
import { FavoriteButton } from '@plm-core/favorites'
import { Link } from 'react-router-dom'
import styles from './teams-view.module.css'
import TeamsEditDrawer from 'teams/components/edit/TeamEditDrawer'
import { useState, useCallback, useContext, useMemo } from 'react'
import { getUserDisplayName } from 'users/utils/userDisplayHelpers'
import { teamsUserListColumnDefs } from 'users/utils/userTableHelpers'
import { PURPOSE_MAP } from '../../utils/teamPurposeHelpers'
import UserViewDrawer from 'users/components/UserViewDrawer'
import { UserContext } from 'App/UserContext'

type Props = {
  team: any
  loading: boolean
  refetchTeam?: () => Promise<any>
}

const TeamsView = ({ team, loading, refetchTeam }: Props) => {
  const { isInternal, sparkUser } = useContext(UserContext)
  const teamDisplayData = team || {}
  const isActive = teamDisplayData.status?.code === 1 ? true : false
  const members = teamDisplayData?.members || []
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const teamBPId = teamDisplayData?.businessPartner?.id
  const isMatchingBPId = useMemo(() => {
    const parsedPartnerId = Number(sparkUser?.partnerId)
    const currentUserBPId = Number.isFinite(parsedPartnerId)
      ? parsedPartnerId
      : null

    const parsedTeamBPId = Number(teamBPId)
    const currentTeamBPId = Number.isFinite(parsedTeamBPId)
      ? parsedTeamBPId
      : null

    return currentUserBPId === currentTeamBPId
  }, [sparkUser, teamBPId])
  const canEditTeam = isInternal || isMatchingBPId

  const handleUserNameClick = useCallback((item: any) => {
    setSelectedUser(item)
  }, [])

  const handleUserViewDrawerClose = useCallback(() => {
    setSelectedUser(null)
  }, [])

  return (
    <>
      {/* Conditionally render the drawer to prevent any stale form data */}
      {isEditDrawerOpen && (
        <TeamsEditDrawer
          team={team}
          isOpen={isEditDrawerOpen}
          onRequestClose={() => setIsEditDrawerOpen(false)}
          refetchTeam={refetchTeam}
        />
      )}
      {selectedUser && (
        <UserViewDrawer
          userProfileId={selectedUser.id}
          isOpen={!!selectedUser}
          onRequestClose={handleUserViewDrawerClose}
          team={team}
          refetchTeam={refetchTeam}
          refetchUser={refetchTeam}
        />
      )}
      <Grid.Container direction="column">
        <Grid.Item size={{ xs: 12 }} className="hc8-mb-3x">
          <Grid.Container align="center">
            <Grid.Item>
              <Button
                size="lg"
                aria-label="Go back"
                isIconOnly
                as={Link}
                to="/teams"
              >
                <Icon svg={ChevronLeftIcon} />
              </Button>
            </Grid.Item>
            <Grid.Item size={{ xs: true }}>
              <Grid.Container align="center">
                <Grid.Item>
                  <Heading size={1}>
                    {loading ? (
                      <div style={{ width: '200px' }}>
                        <Skeleton variant="rectangle" />
                      </div>
                    ) : (
                      team?.teamName
                    )}
                  </Heading>
                </Grid.Item>
                <Grid.Item>
                  {!loading && (
                    <Marker color={isActive ? 'green' : 'grey'}>
                      {team?.status?.description}
                    </Marker>
                  )}
                </Grid.Item>
              </Grid.Container>
            </Grid.Item>
            <Grid.Item>
              <FavoriteButton
                isDisabled={loading}
                entityId={team?.id}
                entityType="team"
                label="Favorite"
                variant="secondary"
              />
            </Grid.Item>
            <Grid.Item>
              {!loading && canEditTeam && (
                <Button
                  variant="primary"
                  onClick={() => setIsEditDrawerOpen(true)}
                >
                  <Icon svg={PencilIcon} />
                  Edit team
                </Button>
              )}
            </Grid.Item>
          </Grid.Container>
        </Grid.Item>
        <Grid.Item size={{ xs: 12 }}>
          <Grid.Container
            align="center"
            justify="space-between"
            className="hc8-mb-6x"
          >
            <Grid.Item>
              <Caption above="Team ID">
                {loading ? <Skeleton variant="rectangle" /> : team?.id}
              </Caption>
            </Grid.Item>
            <Grid.Item>
              <Caption above="Company">
                {loading ? (
                  <Skeleton variant="rectangle" />
                ) : teamDisplayData.teamType === 'EXTERNAL' ? (
                  (teamDisplayData.businessPartner?.name ?? 'N/A')
                ) : (
                  'Target'
                )}
              </Caption>
            </Grid.Item>
            <Grid.Item>
              {(loading || teamDisplayData.teamType === 'EXTERNAL') && (
                <Caption above="Partner type">
                  {loading ? (
                    <Skeleton variant="rectangle" />
                  ) : (
                    (teamDisplayData.partnerType?.description ?? 'N/A')
                  )}
                </Caption>
              )}
            </Grid.Item>
            <Grid.Item>
              <Caption above="Purpose">
                {loading ? (
                  <Skeleton variant="rectangle" />
                ) : (
                  PURPOSE_MAP[teamDisplayData.purpose]?.displayName ||
                  teamDisplayData.purpose
                )}
              </Caption>
            </Grid.Item>
            <Grid.Item>
              <Caption above="Last updated">
                {loading ? (
                  <Skeleton variant="rectangle" />
                ) : (
                  <>
                    <DateFormatter
                      format="MMM DD, YYYY"
                      date={
                        teamDisplayData.updatedAt || new Date().toISOString()
                      }
                    />{' '}
                    by{' '}
                    {getUserDisplayName(
                      teamDisplayData.updatedBy?.firstName,
                      teamDisplayData.updatedBy?.lastName,
                    )}
                  </>
                )}
              </Caption>
            </Grid.Item>
          </Grid.Container>
        </Grid.Item>
        <Grid.Item size={{ xs: 12 }}>
          <Card variant="elevated" className="hc8-pa-6x">
            <Grid.Container direction="column">
              <Grid.Item size={{ xs: 12 }}>
                <Grid.Container align="center">
                  <Grid.Item className={styles['card-icon']}>
                    <Icon svg={PersonGroupIcon} size="lg" />
                  </Grid.Item>
                  <Grid.Item
                    size={{
                      xs: true,
                    }}
                  >
                    <Heading size={5}>Team members</Heading>
                    <div className="hc8-clr-placeholder hc8-fs-100">
                      Manage access and roles for this team.
                    </div>
                  </Grid.Item>
                </Grid.Container>
              </Grid.Item>
              <Grid.Item size={{ xs: 12 }}>
                {!loading && (
                  <Table
                    name="Teams list"
                    alternateRowColor
                    data={{
                      columnDefs: teamsUserListColumnDefs({
                        onUserNameClick: handleUserNameClick,
                      }),
                      rowData: loading ? [] : members,
                    }}
                    mode="client"
                    hasGrid={false}
                    isCornerRounded
                    highlightOnHover={loading ? undefined : 'row'}
                  />
                )}
                {loading && <Skeleton variant="rectangle" height="400px" />}
              </Grid.Item>
            </Grid.Container>
          </Card>
        </Grid.Item>
      </Grid.Container>
    </>
  )
}

export default TeamsView
